import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { getQuestionStats } from '../../services/adminApi';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [questionStats, setQuestionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchQuestionStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminUserStats();
      setStats(data);
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionStats = async () => {
    try {
      const data = await getQuestionStats();
      setQuestionStats(data);
    } catch (err) {
      console.error('Error fetching question stats:', err);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorIcon}>
          <i className="fas fa-exclamation-circle"></i>
        </div>
        <h3 style={styles.errorTitle}>Failed to Load Dashboard</h3>
        <p style={styles.errorText}>{error}</p>
        <button style={styles.retryButton} onClick={fetchStats}>
          <i className="fas fa-redo-alt" style={{ marginRight: '8px' }}></i>
          Try Again
        </button>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: 'fas fa-users',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      // change: '+12%',
      changeType: 'positive',
    },
    {
      title: 'Active Users',
      value: stats?.active_users || 0,
      icon: 'fas fa-user-check',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      // change: '+8%',
      changeType: 'positive',
    },
    {
      title: 'Total Questions',
      value: questionStats?.total_questions || 0,
      icon: 'fas fa-question-circle',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      // change: '+23%',
      changeType: 'positive',
    },
    {
      title: 'Question Types',
      value: questionStats?.by_type ? Object.keys(questionStats.by_type).length : 0,
      icon: 'fas fa-list-alt',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      // change: '+5%',
      changeType: 'positive',
    },
    {
      title: 'Admin Users',
      value: stats?.admin_users || 0,
      icon: 'fas fa-user-shield',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      // change: '0%',
      changeType: 'neutral',
    },
  ];

  const userManagementLinks = [
    {
      title: 'Manage Users',
      description: 'View, edit, and manage all user accounts',
      icon: 'fas fa-users-cog',
      path: '/admin/users',
      color: '#667eea',
    },
    {
      title: 'Create User',
      description: 'Add new users to the system',
      icon: 'fas fa-user-plus',
      path: '/admin/users/create',
      color: '#f5576c',
    },
  ];

  const contentManagementLinks = [
    {
      title: 'Manage Questions',
      description: 'View, edit, and organize questions',
      icon: 'fas fa-question-circle',
      path: '/admin/questions',
      color: '#4facfe',
    },
    {
      title: 'Create Question',
      description: 'Add new questions to database',
      icon: 'fas fa-plus-circle',
      path: '/admin/questions/new',
      color: '#43e97b',
    },
    {
      title: 'Question Statistics',
      description: 'View detailed analytics',
      icon: 'fas fa-chart-bar',
      path: '/admin/questions/stats',
      color: '#fa709a',
    },
    {
      title: 'Bulk Import',
      description: 'Import questions from CSV',
      icon: 'fas fa-file-upload',
      path: '/admin/questions/bulk-import',
      color: '#764ba2',
    },
  ];

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div>
            <div style={styles.welcomeText}>Welcome back,</div>
            <h1 style={styles.title}>Admin Dashboard</h1>
            <p style={styles.subtitle}>Monitor and manage your platform</p>
          </div>
          <div style={styles.headerActions}>
            <button style={styles.refreshButton} onClick={() => { fetchStats(); fetchQuestionStats(); }}>
              <i className="fas fa-sync-alt"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        {statCards.map((card, index) => (
          <div key={index} style={styles.statCard} className="stat-card">
            <div style={styles.statCardHeader}>
              <div style={{...styles.statIconWrapper, background: card.gradient}}>
                <i className={card.icon} style={styles.statCardIcon}></i>
              </div>
              <div style={styles.changeIndicator}>
                <span style={{
                  ...styles.changeText,
                  color: card.changeType === 'positive' ? '#10b981' : 
                         card.changeType === 'negative' ? '#ef4444' : '#6b7280'
                }}>
                  {card.change}
                </span>
              </div>
            </div>
            <div style={styles.statCardBody}>
              <div style={styles.statValue}>{card.value.toLocaleString()}</div>
              <div style={styles.statLabel}>{card.title}</div>
            </div>
          </div>
        ))}
      </div>

      {/* User Management Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionHeaderLeft}>
            <div style={styles.sectionIconWrapper}>
              <i className="fas fa-users" style={styles.sectionIcon}></i>
            </div>
            <div>
              <h2 style={styles.sectionTitle}>User Management</h2>
              <p style={styles.sectionSubtitle}>Manage accounts and permissions</p>
            </div>
          </div>
        </div>
        <div style={styles.cardsGrid}>
          {userManagementLinks.map((link, index) => (
            <div
              key={index}
              style={styles.actionCard}
              onClick={() => navigate(link.path)}
              className="action-card"
            >
              <div style={{...styles.actionIconWrapper, backgroundColor: `${link.color}15`}}>
                <i className={link.icon} style={{...styles.actionIcon, color: link.color}}></i>
              </div>
              <div style={styles.actionContent}>
                <h3 style={styles.actionTitle}>{link.title}</h3>
                <p style={styles.actionDescription}>{link.description}</p>
              </div>
              <div style={styles.actionArrow}>
                <i className="fas fa-arrow-right"></i>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content Management Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionHeaderLeft}>
            <div style={styles.sectionIconWrapper}>
              <i className="fas fa-book" style={styles.sectionIcon}></i>
            </div>
            <div>
              <h2 style={styles.sectionTitle}>Content Management</h2>
              <p style={styles.sectionSubtitle}>Manage questions and materials</p>
            </div>
          </div>
        </div>
        <div style={styles.cardsGrid}>
          {contentManagementLinks.map((link, index) => (
            <div
              key={index}
              style={styles.actionCard}
              onClick={() => navigate(link.path)}
              className="action-card"
            >
              <div style={{...styles.actionIconWrapper, backgroundColor: `${link.color}15`}}>
                <i className={link.icon} style={{...styles.actionIcon, color: link.color}}></i>
              </div>
              <div style={styles.actionContent}>
                <h3 style={styles.actionTitle}>{link.title}</h3>
                <p style={styles.actionDescription}>{link.description}</p>
              </div>
              <div style={styles.actionArrow}>
                <i className="fas fa-arrow-right"></i>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .stat-card {
          animation: fadeIn 0.5s ease-out forwards;
        }

        .stat-card:nth-child(1) { animation-delay: 0.1s; }
        .stat-card:nth-child(2) { animation-delay: 0.2s; }
        .stat-card:nth-child(3) { animation-delay: 0.3s; }
        .stat-card:nth-child(4) { animation-delay: 0.4s; }
        .stat-card:nth-child(5) { animation-delay: 0.5s; }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
        }

        .action-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
          border-color: rgba(102, 126, 234, 0.3);
        }

        .action-card:hover .action-arrow {
          transform: translateX(4px);
        }

        .action-card:active {
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f8fafc',
    minHeight: '100vh',
    padding: '24px',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    gap: '24px',
    backgroundColor: '#f8fafc',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    fontSize: '16px',
    color: '#64748b',
    fontWeight: '500',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    gap: '20px',
    backgroundColor: '#f8fafc',
    padding: '24px',
  },
  errorIcon: {
    fontSize: '64px',
    color: '#ef4444',
    marginBottom: '8px',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0',
  },
  errorText: {
    fontSize: '16px',
    color: '#64748b',
    textAlign: 'center',
    maxWidth: '400px',
  },
  retryButton: {
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 32px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  },
  header: {
    backgroundColor: 'transparent',
    marginBottom: '32px',
    maxWidth: '1400px',
    margin: '0 auto 32px auto',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  welcomeText: {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '500',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  title: {
    fontSize: '36px',
    fontWeight: '800',
    margin: '0 0 8px 0',
    color: '#1e293b',
    letterSpacing: '-0.5px',
    lineHeight: '1.2',
  },
  subtitle: {
    fontSize: '16px',
    margin: '0',
    color: '#64748b',
    fontWeight: '400',
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  refreshButton: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    backgroundColor: 'white',
    color: '#64748b',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    transition: 'all 0.2s',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '20px',
    marginBottom: '40px',
    maxWidth: '1400px',
    margin: '0 auto 40px auto',
  },
  statCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid #f1f5f9',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
  },
  statCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  statIconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  statCardIcon: {
    fontSize: '20px',
    color: 'white',
  },
  changeIndicator: {
    padding: '4px 10px',
    borderRadius: '8px',
    backgroundColor: '#f0fdf4',
  },
  changeText: {
    fontSize: '13px',
    fontWeight: '600',
  },
  statCardBody: {
    marginTop: '4px',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '800',
    color: '#1e293b',
    lineHeight: '1',
    marginBottom: '8px',
    letterSpacing: '-0.5px',
  },
  statLabel: {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '500',
  },
  section: {
    maxWidth: '1400px',
    margin: '0 auto 40px auto',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  sectionIconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #f1f5f9',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  },
  sectionIcon: {
    fontSize: '20px',
    color: '#667eea',
  },
  sectionTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0',
    letterSpacing: '-0.3px',
  },
  sectionSubtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '20px',
  },
  actionCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid #f1f5f9',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    position: 'relative',
    overflow: 'hidden',
  },
  actionIconWrapper: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  actionIcon: {
    fontSize: '24px',
  },
  actionContent: {
    flex: 1,
    minWidth: 0,
  },
  actionTitle: {
    fontSize: '17px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 6px 0',
    letterSpacing: '-0.2px',
  },
  actionDescription: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0',
    lineHeight: '1.5',
  },
  actionArrow: {
    fontSize: '18px',
    color: '#cbd5e1',
    transition: 'all 0.2s',
    flexShrink: 0,
  },
};

export default AdminDashboard;







































// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { apiService } from '../../services/api';
// import { getQuestionStats } from '../../services/adminApi';

// const AdminDashboard = () => {
//   const navigate = useNavigate();
//   const [stats, setStats] = useState(null);
//   const [questionStats, setQuestionStats] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     fetchStats();
//     fetchQuestionStats();
//   }, []);

//   const fetchStats = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const data = await apiService.getAdminUserStats();
//       setStats(data);
//     } catch (err) {
//       console.error('Error fetching admin stats:', err);
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchQuestionStats = async () => {
//     try {
//       const data = await getQuestionStats();
//       setQuestionStats(data);
//     } catch (err) {
//       console.error('Error fetching question stats:', err);
//     }
//   };

//   if (loading) {
//     return (
//       <div style={styles.loadingContainer}>
//         <div style={styles.spinner}></div>
//         <p style={styles.loadingText}>Loading admin dashboard...</p>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div style={styles.errorContainer}>
//         <i className="fas fa-exclamation-triangle" style={styles.errorIcon}></i>
//         <p style={styles.errorText}>{error}</p>
//         <button style={styles.retryButton} onClick={fetchStats}>
//           Retry
//         </button>
//       </div>
//     );
//   }

//   const statCards = [
//     {
//       title: 'Total Users',
//       value: stats?.total_users || 0,
//       icon: 'fas fa-users',
//       color: '#2c5aa0',
//       bgColor: '#e3f2fd',
//     },
//     {
//       title: 'Active Users',
//       value: stats?.active_users || 0,
//       icon: 'fas fa-user-check',
//       color: '#28a745',
//       bgColor: '#d4edda',
//     },
//     {
//       title: 'Total Questions',
//       value: questionStats?.total_questions || 0,
//       icon: 'fas fa-question-circle',
//       color: '#f39c12',
//       bgColor: '#fff8e1',
//     },
//     {
//       title: 'Question Types',
//       value: questionStats?.by_type ? Object.keys(questionStats.by_type).length : 0,
//       icon: 'fas fa-list-alt',
//       color: '#17a2b8',
//       bgColor: '#e0f7fa',
//     },
//     {
//       title: 'Admin Users',
//       value: stats?.admin_users || 0,
//       icon: 'fas fa-user-shield',
//       color: '#6f42c1',
//       bgColor: '#e2d9f3',
//     },
//   ];

//   const userManagementLinks = [
//     {
//       title: 'Manage Users',
//       description: 'View, edit, and manage all users',
//       icon: 'fas fa-users-cog',
//       path: '/admin/users',
//       color: '#2c5aa0',
//     },
//     {
//       title: 'Create User',
//       description: 'Add a new user to the system',
//       icon: 'fas fa-user-plus',
//       path: '/admin/users/create',
//       color: '#28a745',
//     },
//   ];

//   const contentManagementLinks = [
//     {
//       title: 'Manage Questions',
//       description: 'View, edit, and manage all questions',
//       icon: 'fas fa-question-circle',
//       path: '/admin/questions',
//       color: '#f39c12',
//     },
//     {
//       title: 'Create Question',
//       description: 'Add a new question to the database',
//       icon: 'fas fa-plus-circle',
//       path: '/admin/questions/new',
//       color: '#17a2b8',
//     },
//     {
//       title: 'Question Statistics',
//       description: 'View analytics and statistics',
//       icon: 'fas fa-chart-bar',
//       path: '/admin/questions/stats',
//       color: '#28a745',
//     },
//     {
//       title: 'Bulk Import',
//       description: 'Import questions from CSV file',
//       icon: 'fas fa-file-upload',
//       path: '/admin/questions/bulk-import',
//       color: '#6f42c1',
//     },
//   ];

//   return (
//     <div style={styles.container}>
//       <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
//       <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      
//       <div style={styles.header}>
//         <div style={styles.headerContent}>
//           <div style={styles.titleSection}>
//             <h1 style={styles.title}>Admin Dashboard</h1>
//             <p style={styles.subtitle}>Manage users, questions, and monitor system activity</p>
//           </div>
//         </div>
//       </div>

//       <div style={styles.statsGrid}>
//         {statCards.map((card, index) => (
//           <div key={index} style={styles.statCard}>
//             <div style={{...styles.statIcon, backgroundColor: card.bgColor}}>
//               <i className={card.icon} style={{...styles.icon, color: card.color}}></i>
//             </div>
//             <div style={styles.statContent}>
//               <div style={styles.statValue}>{card.value}</div>
//               <div style={styles.statLabel}>{card.title}</div>
//             </div>
//           </div>
//         ))}
//       </div>

//       {/* User Management Section */}
//       <div style={styles.quickLinksSection}>
//         <div style={styles.sectionHeader}>
//           <div style={styles.sectionTitleWrapper}>
//             <i className="fas fa-users" style={styles.sectionIcon}></i>
//             <h2 style={styles.sectionTitle}>User Management</h2>
//           </div>
//           <div style={styles.sectionDescription}>Manage user accounts and permissions</div>
//         </div>
//         <div style={styles.quickLinksGrid}>
//           {userManagementLinks.map((link, index) => (
//             <div
//               key={index}
//               style={styles.quickLinkCard}
//               onClick={() => navigate(link.path)}
//               className="quick-link-card"
//             >
//               <div style={{...styles.quickLinkIcon, backgroundColor: `${link.color}15`}}>
//                 <i className={link.icon} style={{...styles.icon, color: link.color}}></i>
//               </div>
//               <div style={styles.quickLinkContent}>
//                 <h3 style={styles.quickLinkTitle}>{link.title}</h3>
//                 <p style={styles.quickLinkDescription}>{link.description}</p>
//               </div>
//               <i className="fas fa-arrow-right" style={styles.arrowIcon}></i>
//             </div>
//           ))}
//         </div>
//       </div>

//       {/* Content Management Section */}
//       <div style={styles.quickLinksSection}>
//         <div style={styles.sectionHeader}>
//           <div style={styles.sectionTitleWrapper}>
//             <i className="fas fa-book" style={styles.sectionIcon}></i>
//             <h2 style={styles.sectionTitle}>Content Management</h2>
//           </div>
//           <div style={styles.sectionDescription}>Manage questions and study materials</div>
//         </div>
//         <div style={styles.quickLinksGrid}>
//           {contentManagementLinks.map((link, index) => (
//             <div
//               key={index}
//               style={styles.quickLinkCard}
//               onClick={() => navigate(link.path)}
//               className="quick-link-card"
//             >
//               <div style={{...styles.quickLinkIcon, backgroundColor: `${link.color}15`}}>
//                 <i className={link.icon} style={{...styles.icon, color: link.color}}></i>
//               </div>
//               <div style={styles.quickLinkContent}>
//                 <h3 style={styles.quickLinkTitle}>{link.title}</h3>
//                 <p style={styles.quickLinkDescription}>{link.description}</p>
//               </div>
//               <i className="fas fa-arrow-right" style={styles.arrowIcon}></i>
//             </div>
//           ))}
//         </div>
//       </div>

//       <style>{`
//         @keyframes spin {
//           0% { transform: rotate(0deg); }
//           100% { transform: rotate(360deg); }
//         }

//         .quick-link-card:hover {
//           transform: translateY(-2px);
//           box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
//           border-color: #2c5aa0;
//         }

//         .quick-link-card:hover .arrow-icon {
//           transform: translateX(5px);
//         }
//       `}</style>
//     </div>
//   );
// };

// const styles = {
//   container: {
//     backgroundColor: '#f8f9fa',
//     minHeight: '100vh',
//     padding: '20px',
//     fontFamily: '"Open Sans", sans-serif',
//   },
//   loadingContainer: {
//     display: 'flex',
//     flexDirection: 'column',
//     justifyContent: 'center',
//     alignItems: 'center',
//     height: '60vh',
//     gap: '20px',
//   },
//   spinner: {
//     width: '50px',
//     height: '50px',
//     border: '4px solid #f3f3f3',
//     borderTop: '4px solid #2c5aa0',
//     borderRadius: '50%',
//     animation: 'spin 1s linear infinite',
//   },
//   loadingText: {
//     fontSize: '18px',
//     color: '#2c5aa0',
//     fontWeight: '500',
//   },
//   errorContainer: {
//     display: 'flex',
//     flexDirection: 'column',
//     justifyContent: 'center',
//     alignItems: 'center',
//     height: '60vh',
//     gap: '20px',
//   },
//   errorIcon: {
//     fontSize: '48px',
//     color: '#dc3545',
//   },
//   errorText: {
//     fontSize: '16px',
//     color: '#666',
//   },
//   retryButton: {
//     backgroundColor: '#2c5aa0',
//     color: 'white',
//     border: 'none',
//     borderRadius: '8px',
//     padding: '12px 24px',
//     fontSize: '16px',
//     fontWeight: '600',
//     cursor: 'pointer',
//     transition: 'all 0.2s',
//   },
//   header: {
//     backgroundColor: '#ffffff',
//     borderRadius: '10px',
//     padding: '30px',
//     marginBottom: '25px',
//     boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
//     border: '1px solid #e0e0e0',
//   },
//   headerContent: {
//     display: 'flex',
//     justifyContent: 'space-between',
//     alignItems: 'center',
//     maxWidth: '1200px',
//     margin: '0 auto',
//   },
//   titleSection: {
//     flex: 1,
//   },
//   title: {
//     fontSize: '32px',
//     fontWeight: '600',
//     margin: '0 0 8px 0',
//     color: '#2c5aa0',
//     letterSpacing: '0px',
//     lineHeight: '1.2',
//   },
//   subtitle: {
//     fontSize: '16px',
//     margin: '0',
//     color: '#666666',
//     letterSpacing: '0.25px',
//     lineHeight: '1.5',
//   },
//   statsGrid: {
//     display: 'grid',
//     gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
//     gap: '20px',
//     marginBottom: '30px',
//     maxWidth: '1200px',
//     margin: '0 auto 30px auto',
//   },
//   statCard: {
//     backgroundColor: '#ffffff',
//     borderRadius: '10px',
//     padding: '24px',
//     display: 'flex',
//     alignItems: 'center',
//     gap: '16px',
//     boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
//     border: '1px solid #e0e0e0',
//   },
//   statIcon: {
//     width: '56px',
//     height: '56px',
//     borderRadius: '12px',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     flexShrink: 0,
//   },
//   icon: {
//     fontSize: '24px',
//   },
//   statContent: {
//     flex: 1,
//   },
//   statValue: {
//     fontSize: '28px',
//     fontWeight: '700',
//     color: '#333333',
//     lineHeight: '1.2',
//     marginBottom: '4px',
//   },
//   statLabel: {
//     fontSize: '14px',
//     color: '#666666',
//     fontWeight: '500',
//   },
//   quickLinksSection: {
//     maxWidth: '1200px',
//     margin: '0 auto 40px auto',
//   },
//   sectionHeader: {
//     marginBottom: '20px',
//     paddingBottom: '15px',
//     borderBottom: '2px solid #f0f0f0',
//   },
//   sectionTitleWrapper: {
//     display: 'flex',
//     alignItems: 'center',
//     gap: '12px',
//     marginBottom: '8px',
//   },
//   sectionIcon: {
//     fontSize: '24px',
//     color: '#2c5aa0',
//   },
//   sectionTitle: {
//     fontSize: '24px',
//     fontWeight: '600',
//     color: '#2c5aa0',
//     margin: 0,
//   },
//   sectionDescription: {
//     fontSize: '14px',
//     color: '#666',
//     marginLeft: '36px',
//   },
//   quickLinksGrid: {
//     display: 'grid',
//     gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
//     gap: '20px',
//   },
//   quickLinkCard: {
//     backgroundColor: '#ffffff',
//     borderRadius: '10px',
//     padding: '24px',
//     display: 'flex',
//     alignItems: 'center',
//     gap: '16px',
//     cursor: 'pointer',
//     transition: 'all 0.2s',
//     border: '1px solid #e0e0e0',
//     boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
//   },
//   quickLinkIcon: {
//     width: '56px',
//     height: '56px',
//     borderRadius: '12px',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     flexShrink: 0,
//   },
//   quickLinkContent: {
//     flex: 1,
//   },
//   quickLinkTitle: {
//     fontSize: '18px',
//     fontWeight: '600',
//     color: '#333333',
//     margin: '0 0 4px 0',
//   },
//   quickLinkDescription: {
//     fontSize: '14px',
//     color: '#666666',
//     margin: '0',
//   },
//   arrowIcon: {
//     fontSize: '20px',
//     color: '#2c5aa0',
//     transition: 'transform 0.2s',
//   },
// };

// export default AdminDashboard;
